import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, LogOut, Refrigerator, AlertTriangle, Clock, TrendingUp, BarChart3, Users, Crown, Package, DollarSign, HelpCircle, UserPlus, Edit, ChefHat } from 'lucide-react';
import { db } from '../firebase/config';
import { collection, query, where, orderBy, onSnapshot, getDocs, getDoc, limit, doc, updateDoc } from 'firebase/firestore';
import { EMOJIS, getCategoryEmoji } from '../constants/emojis';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useSubscriptionLimits } from '../hooks/useSubscription';
import { CommunityStatsWidget } from '../components/CommunityStatsWidget';
import { BadgesDisplay } from '../components/BadgesDisplay';

export function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [usedItems, setUsedItems] = useState<any[]>([]);
  const [wastedItems, setWastedItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortByUrgency, setSortByUrgency] = useState(true);
  const [trialDaysLeft, setTrialDaysLeft] = useState<number | null>(null);
  const [isTrialActive, setIsTrialActive] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('inactive');
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [hasHousehold, setHasHousehold] = useState(true);
  const [householdBadges, setHouseholdBadges] = useState<string[]>([]);
  const [householdStats, setHouseholdStats] = useState<any>(null);
  const subscription = useSubscriptionLimits();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    async function loadItems() {
      try {
        // Get user's household
        const householdsQuery = query(
          collection(db, 'households'),
          where('members', 'array-contains', user!.uid),
          limit(1)
        );

        const householdsSnapshot = await getDocs(householdsQuery);

        if (householdsSnapshot.empty) {
          setHasHousehold(false);
          setLoading(false);
          return;
        }

        setHasHousehold(true);

        const householdId = householdsSnapshot.docs[0].id;
        const householdData = householdsSnapshot.docs[0].data();

        // Get user's subscription status from their user document
        const userDocRef = doc(db, 'users', user!.uid);
        const userDocSnap = await getDoc(userDocRef);

        let userSubscriptionStatus = 'inactive';
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          userSubscriptionStatus = userData.subscriptionStatus || 'inactive';
        }

        // Set subscription status - prioritize user status over household status
        const finalStatus = userSubscriptionStatus === 'pro' || userSubscriptionStatus === 'trialing' 
          ? userSubscriptionStatus 
          : (householdData.subscriptionStatus || 'inactive');
        
        setSubscriptionStatus(finalStatus);

        // Load household badges and stats
        setHouseholdBadges(householdData.badges || []);
        setHouseholdStats(householdData.stats || {
          totalItemsSaved: 0,
          totalItemsWasted: 0,
          totalMoneySaved: 0,
          totalCO2Saved: 0,
          currentZeroWasteStreak: 0,
        });

        // Calculate trial days remaining
        if (householdData.trialEndsAt && finalStatus !== 'active' && finalStatus !== 'pro') {
          const trialEndDate = new Date(householdData.trialEndsAt);
          const now = new Date();
          const daysLeft = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysLeft > 0) {
            setTrialDaysLeft(daysLeft);
            setIsTrialActive(true);
          } else {
            setTrialDaysLeft(0);
            setIsTrialActive(false);
          }
        }

        // Listen to ACTIVE items in real-time
        const activeItemsQuery = query(
          collection(db, 'items'),
          where('householdId', '==', householdId),
          where('status', '==', 'active'),
          orderBy('expirationDate', 'asc')
        );

        const unsubscribeActive = onSnapshot(activeItemsQuery, (snapshot) => {
          const itemsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setItems(itemsData);
          setLoading(false);
        });

        // Listen to USED items in real-time (for $ Saved calculation)
        const usedItemsQuery = query(
          collection(db, 'items'),
          where('householdId', '==', householdId),
          where('status', '==', 'used')
        );

        const unsubscribeUsed = onSnapshot(usedItemsQuery, (snapshot) => {
          const usedData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setUsedItems(usedData);
        });

        // Listen to WASTED items in real-time (for $ Wasted calculation)
        const wastedItemsQuery = query(
          collection(db, 'items'),
          where('householdId', '==', householdId),
          where('status', '==', 'wasted')
        );

        const unsubscribeWasted = onSnapshot(wastedItemsQuery, (snapshot) => {
          const wastedData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setWastedItems(wastedData);
        });

        return () => {
          unsubscribeActive();
          unsubscribeUsed();
          unsubscribeWasted();
        };
      } catch (error) {
        console.error('Error loading items:', error);
        setLoading(false);
      }
    }

    loadItems();
  }, [user, navigate]);

  // Calculate $ Saved (from items marked as "used")
  const moneySaved = usedItems.reduce((total, item) => {
    return total + (item.price || 0);
  }, 0);

  // Calculate $ Wasted (from items marked as "wasted")
  const moneyWasted = wastedItems.reduce((total, item) => {
    return total + (item.price || 0);
  }, 0);

  // Calculate urgency level for an item
  function getUrgency(expirationDate: string) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expDate = new Date(expirationDate);
    expDate.setHours(0, 0, 0, 0);
    const daysUntilExpiration = Math.ceil((expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiration < 0) {
      return {
        level: 'expired',
        color: '#E63946',
        bgColor: '#FEE2E2',
        borderColor: '#E63946',
        label: 'Expired',
        icon: <AlertTriangle className="w-4 h-4" />,
        days: daysUntilExpiration,
        sortOrder: 0
      };
    } else if (daysUntilExpiration <= 2) {
      return {
        level: 'critical',
        color: '#FF8F76',
        bgColor: '#FFF4ED',
        borderColor: '#FF8F76',
        label: `${daysUntilExpiration} day${daysUntilExpiration === 1 ? '' : 's'} left`,
        icon: <AlertTriangle className="w-4 h-4" />,
        days: daysUntilExpiration,
        sortOrder: 1
      };
    } else if (daysUntilExpiration <= 5) {
      return {
        level: 'warning',
        color: '#FF8F76',
        bgColor: '#FFF4ED',
        borderColor: '#FF8F76',
        label: `${daysUntilExpiration} days left`,
        icon: <Clock className="w-4 h-4" />,
        days: daysUntilExpiration,
        sortOrder: 2
      };
    } else if (daysUntilExpiration <= 7) {
      return {
        level: 'caution',
        color: '#FFD166',
        bgColor: '#FFFBEB',
        borderColor: '#FFD166',
        label: `${daysUntilExpiration} days left`,
        icon: <Clock className="w-4 h-4" />,
        days: daysUntilExpiration,
        sortOrder: 3
      };
    } else {
      return {
        level: 'safe',
        color: '#72C491',
        bgColor: '#F0FDF4',
        borderColor: '#72C491',
        label: `${daysUntilExpiration} days left`,
        icon: <Clock className="w-4 h-4" />,
        days: daysUntilExpiration,
        sortOrder: 4
      };
    }
  }

  // Calculate urgency statistics
  const urgencyStats = items.reduce((stats, item) => {
    const urgency = getUrgency(item.expirationDate);
    if (urgency.level === 'expired') stats.expired++;
    else if (urgency.level === 'critical' || urgency.level === 'warning') stats.expiringSoon++;
    return stats;
  }, { expired: 0, expiringSoon: 0 });

  // Sort items by urgency or date added
  const sortedItems = sortByUrgency
    ? [...items].sort((a, b) => {
        const urgencyA = getUrgency(a.expirationDate);
        const urgencyB = getUrgency(b.expirationDate);
        if (urgencyA.sortOrder !== urgencyB.sortOrder) {
          return urgencyA.sortOrder - urgencyB.sortOrder;
        }
        return urgencyA.days - urgencyB.days;
      })
    : [...items].sort((a, b) => {
        // Sort by date added (newest first)
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA;
      });

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  }

  async function handleMarkAsUsed(itemId: string) {
    try {
      await updateDoc(doc(db, 'items', itemId), {
        status: 'used',
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error marking item as used:', error);
    }
  }

  async function handleMarkAsWasted(itemId: string) {
    try {
      await updateDoc(doc(db, 'items', itemId), {
        status: 'wasted',
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error marking item as wasted:', error);
    }
  }

  async function handleManageSubscription() {
    try {
      setManagingSubscription(true);
      
      const functions = getFunctions();
      const createPortalSession = httpsCallable(functions, 'createPortalSession');
      
      const result = await createPortalSession({
        returnUrl: window.location.origin + '/dashboard'
      });
      
      const data = result.data as { url: string };
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (err: any) {
      console.error('Error opening billing portal:', err);
      alert('Failed to open subscription management. Please try again.');
      setManagingSubscription(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-bg flex items-center justify-center">
        <div className="text-center">
          <img src="/logo.png" alt="Loading" className="w-20 h-20 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading your fridge...</p>
        </div>
      </div>
    );
  }

  // Show "No Household" UI if user isn't in a household
  if (!hasHousehold) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-primary-green to-primary-mint rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Users className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">No Household Found</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              You're not currently part of a household. Join an existing household with a code or contact support.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <Link
                to="/join-household"
                className="flex items-center justify-center gap-2 px-6 py-4 bg-primary-green text-white rounded-xl font-semibold hover:bg-primary-green/90 transition-all shadow-lg hover:shadow-xl"
              >
                <UserPlus className="w-5 h-5" />
                Join with Code
              </Link>
              <Link
                to="/household-settings"
                className="flex items-center justify-center gap-2 px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
              >
                <Users className="w-5 h-5" />
                View Settings
              </Link>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm text-blue-900">
                <strong>Need help?</strong> Contact support or ask your household owner to send you an invitation email or share their join code.
              </p>
            </div>

            <button
              onClick={handleLogout}
              className="mt-6 text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-bg">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="FridgeLife" className="w-10 h-10" />
              <div>
                <h1 className="text-xl font-bold text-primary-green">FridgeLife</h1>
                <p className="text-xs text-gray-500">Welcome back!</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/cook-before-expires"
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-primary-green hover:bg-gray-100 rounded-lg transition-all"
                title="Cook Before It Expires"
              >
                <ChefHat className="w-4 h-4" />
                <span className="hidden sm:inline">Cook</span>
              </Link>
              <Link
  to={subscription.hasAdvancedAnalytics ? "/advanced-analytics" : "/analytics"}
  className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-primary-green hover:bg-gray-100 rounded-lg transition-all"
  title={subscription.hasAdvancedAnalytics ? "View Advanced Analytics" : "View Analytics"}
>
  <BarChart3 className="w-4 h-4" />
  <span className="hidden sm:inline">
    {subscription.hasAdvancedAnalytics ? "Advanced Analytics" : "Analytics"}
  </span>
</Link>
              <Link
                to="/how-to-use"
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-primary-green hover:bg-gray-100 rounded-lg transition-all"
                title="How to Use FridgeLife"
              >
                <HelpCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Help</span>
              </Link>
              <Link
                to="/household-settings"
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-primary-green hover:bg-gray-100 rounded-lg transition-all"
                title="Household Settings"
              >
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Household</span>
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Log Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Trial Countdown Banner */}
      {isTrialActive && trialDaysLeft !== null && (
        <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 border-b border-yellow-700">
          <div className="container mx-auto px-4 py-3">
            <div className="max-w-4xl mx-auto flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm sm:text-base">
                    {trialDaysLeft === 0 
                      ? "Your free trial ends today!" 
                      : `${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} left in your free trial`
                    }
                  </p>
                  <p className="text-white/90 text-xs sm:text-sm">
                    Upgrade to Premium for unlimited items and features
                  </p>
                </div>
              </div>
              <Link
                to="/pricing"
                className="bg-white text-yellow-600 px-4 py-2 rounded-lg font-semibold hover:bg-yellow-50 transition-all shadow-md text-sm whitespace-nowrap"
              >
                Upgrade Now
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Premium Membership Banner */}
      {(subscriptionStatus === 'active' || subscriptionStatus === 'pro') && (
        <div className="bg-gradient-to-r from-primary-green via-primary-mint to-primary-green border-b border-primary-green/30">
          <div className="container mx-auto px-4 py-3">
            <div className="max-w-4xl mx-auto flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center">
                  <Crown className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm sm:text-base">
                    {EMOJIS.PLANT} Premium Member
                  </p>
                  <p className="text-white/90 text-xs sm:text-sm">
                    You have unlimited access to all features
                  </p>
                </div>
              </div>
              <button
                onClick={handleManageSubscription}
                disabled={managingSubscription}
                className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-lg font-semibold hover:bg-white/30 transition-all border border-white/30 text-sm whitespace-nowrap disabled:opacity-50"
              >
                {managingSubscription ? 'Opening...' : 'Manage Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Urgency Alert Banner - Only show if there are urgent items */}
          {(urgencyStats.expired > 0 || urgencyStats.expiringSoon > 0) && (
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-l-4 border-warning">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-warning/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-warning" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Action Required!</h3>
                  <p className="text-gray-600">
                    {urgencyStats.expired > 0 && (
                      <span className="font-semibold text-critical">
                        {urgencyStats.expired} item{urgencyStats.expired > 1 ? 's' : ''} expired
                      </span>
                    )}
                    {urgencyStats.expired > 0 && urgencyStats.expiringSoon > 0 && <span> {EMOJIS.BULLET} </span>}
                    {urgencyStats.expiringSoon > 0 && (
                      <span className="font-semibold text-warning">
                        {urgencyStats.expiringSoon} item{urgencyStats.expiringSoon > 1 ? 's' : ''} expiring soon
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Stats Card */}
          <div className="bg-gradient-to-br from-primary-green to-primary-green/80 rounded-2xl shadow-xl p-8 mb-8 text-white">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">Your Fridge</h2>
                <p className="text-white/80">Track what you have, save what matters</p>
              </div>
              <Refrigerator className="w-16 h-16 text-white/30" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                <div className="text-2xl sm:text-3xl font-bold">{items.length}</div>
                <div className="text-xs sm:text-sm text-white/80">Active Items</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                <div className="text-2xl sm:text-3xl font-bold">{urgencyStats.expiringSoon + urgencyStats.expired}</div>
                <div className="text-xs sm:text-sm text-white/80">Expiring Soon</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 col-span-2 sm:col-span-1">
                <div className="text-2xl sm:text-3xl font-bold">${moneySaved.toFixed(2)}</div>
                <div className="text-xs sm:text-sm text-white/80">Saved</div>
              </div>
            </div>
          </div>

          {/* Community Stats Widget */}
          <div className="mb-8">
            <CommunityStatsWidget />
          </div>

          {/* Badges Display */}
          {householdBadges.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Your Achievements</h3>
              <BadgesDisplay earnedBadgeIds={householdBadges} compact />
            </div>
          )}

          {/* Household Stats */}
          {householdStats && householdStats.totalItemsSaved > 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Your Stats</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-safe/10 rounded-xl">
                  <div className="text-3xl font-bold text-safe">{householdStats.totalItemsSaved}</div>
                  <div className="text-sm text-gray-600">Items Saved</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <div className="text-3xl font-bold text-blue-600">${householdStats.totalMoneySaved.toFixed(2)}</div>
                  <div className="text-sm text-gray-600">Money Saved</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <div className="text-3xl font-bold text-green-600">{householdStats.totalCO2Saved} kg</div>
                  <div className="text-sm text-gray-600">CO₂ Saved</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-xl">
                  <div className="text-3xl font-bold text-yellow-600">{householdStats.currentZeroWasteStreak}</div>
                  <div className="text-sm text-gray-600">Day Streak</div>
                </div>
              </div>
            </div>
          )}

          {/* Savings Summary Card - Only show if there are used or wasted items */}
          {(usedItems.length > 0 || wastedItems.length > 0) && (
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary-mint/30 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary-green" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Your Impact</h3>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-safe/10 rounded-xl">
                  <div className="text-2xl font-bold text-safe">${moneySaved.toFixed(2)}</div>
                  <div className="text-sm text-gray-600 mt-1">Money Saved</div>
                  <div className="text-xs text-gray-500 mt-1">{usedItems.length} items used</div>
                </div>
                <div className="text-center p-4 bg-critical/10 rounded-xl">
                  <div className="text-2xl font-bold text-critical">${moneyWasted.toFixed(2)}</div>
                  <div className="text-sm text-gray-600 mt-1">Money Wasted</div>
                  <div className="text-xs text-gray-500 mt-1">{wastedItems.length} items wasted</div>
                </div>
                <div className="text-center p-4 bg-primary-green/10 rounded-xl">
                  <div className="text-2xl font-bold text-primary-green">
                    {((usedItems.length / (usedItems.length + wastedItems.length)) * 100 || 0).toFixed(0)}%
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Success Rate</div>
                  <div className="text-xs text-gray-500 mt-1">Items saved vs wasted</div>
                </div>
              </div>
            </div>
          )}

          {/* Items List Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Current Items</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSortByUrgency(!sortByUrgency)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  sortByUrgency 
                    ? 'bg-primary-green text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {sortByUrgency ? 'Urgency Sort' : 'Date Added'}
              </button>
              <Link
                to="/add-item"
                className="flex items-center gap-2 bg-primary-green text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-green/90 transition-all shadow-lg hover:shadow-xl"
              >
                <Plus className="w-5 h-5" />
                Add Item
              </Link>
            </div>
          </div>

          {/* Items Grid */}
          {items.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <Refrigerator className="w-20 h-20 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Your fridge is empty</h3>
              <p className="text-gray-600 mb-6">Start tracking your food items to reduce waste and save money</p>
              <Link
                to="/add-item"
                className="inline-flex items-center gap-2 bg-primary-green text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-green/90 transition-all"
              >
                <Plus className="w-5 h-5" />
                Add Your First Item
              </Link>
            </div>
          ) : (
            <div className="grid gap-4">
              {sortedItems.map((item) => {
                const urgency = getUrgency(item.expirationDate);
                return (
                  <div
                    key={item.id}
                    className="bg-white rounded-xl shadow-md p-4 sm:p-5 border-l-4 hover:shadow-lg transition-all"
                    style={{ borderColor: urgency.borderColor }}
                  >
                    {/* Mobile: Stack vertically, Desktop: Side by side */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                        {/* Photo or Category Icon */}
                        {item.photoURL ? (
                          <img
                            src={item.photoURL}
                            alt={item.name}
                            className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl object-cover flex-shrink-0 border-2 border-gray-200"
                          />
                        ) : (
                          <div
                            className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center text-2xl sm:text-3xl flex-shrink-0"
                            style={{ backgroundColor: urgency.bgColor }}
                          >
                            {getCategoryEmoji(item.category)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 truncate">{item.name}</h3>
                          {item.brand && (
                            <p className="text-xs text-gray-500 mb-1 truncate">{item.brand}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-2">
                            <span className="flex items-center gap-1">
                              <Package className="w-4 h-4" />
                              {item.quantity} {item.quantity === 1 ? 'item' : 'items'}
                            </span>
                            {item.price > 0 && (
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-4 h-4" />
                                ${item.price.toFixed(2)}
                              </span>
                            )}
                            <span className="capitalize bg-gray-100 px-3 py-1 rounded-full">
                              {item.category}
                            </span>
                          </div>
                          {/* Allergen Warning */}
                          {item.allergens && item.allergens.length > 0 && (
                            <div className="mb-2 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
                              <p className="text-xs font-semibold text-red-800 mb-0.5">⚠️ Allergens:</p>
                              <p className="text-xs text-red-700">{item.allergens.join(', ')}</p>
                            </div>
                          )}
                          {/* Nutrition Info (Brief) */}
                          {item.nutrition && (item.nutrition.calories || item.nutrition.protein) && (
                            <div className="text-xs text-gray-600 flex flex-wrap gap-2">
                              {item.nutrition.calories && (
                                <span className="bg-gray-50 px-2 py-1 rounded">
                                  {item.nutrition.calories} kcal
                                </span>
                              )}
                              {item.nutrition.protein && (
                                <span className="bg-gray-50 px-2 py-1 rounded">
                                  {item.nutrition.protein}g protein
                                </span>
                              )}
                              {item.nutrition.carbohydrates && (
                                <span className="bg-gray-50 px-2 py-1 rounded">
                                  {item.nutrition.carbohydrates}g carbs
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions: Horizontal on mobile, Vertical on desktop */}
                      <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 sm:gap-3 flex-shrink-0 w-full sm:w-auto">
                        {/* Urgency Badge */}
                        <div
                          className="px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm flex items-center gap-1 sm:gap-2 whitespace-nowrap"
                          style={{
                            backgroundColor: urgency.bgColor,
                            color: urgency.color
                          }}
                        >
                          <span className="text-sm sm:text-base">{urgency.icon}</span>
                          <span className="hidden sm:inline">{urgency.label}</span>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-1.5 sm:gap-2">
                          <button
                            onClick={() => navigate(`/edit-item/${item.id}`)}
                            className="px-2 sm:px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all flex items-center gap-1 touch-manipulation"
                            title="Edit"
                          >
                            <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            onClick={() => handleMarkAsUsed(item.id)}
                            className="px-2 sm:px-3 py-2 text-xs font-medium text-safe bg-safe/10 hover:bg-safe/20 rounded-lg transition-all touch-manipulation"
                            title="Mark as Used"
                          >
                            {EMOJIS.CHECK}
                          </button>
                          <button
                            onClick={() => handleMarkAsWasted(item.id)}
                            className="px-2 sm:px-3 py-2 text-xs font-medium text-critical bg-critical/10 hover:bg-critical/20 rounded-lg transition-all touch-manipulation"
                            title="Mark as Wasted"
                          >
                            {EMOJIS.TRASH}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
